/**
 * Client-side mirror of de-hub's `normalize_entity_name` (broker_deals/model.py).
 *
 * Lower-cases, strips punctuation, collapses internal whitespace runs, and
 * trims — so "Acme, L.L.C." and "acme  llc" collapse to the same key. The
 * server owns this rule; this copy exists only so the UI can PREDICT a
 * server-side name match without a round-trip, specifically to warn an admin
 * that the operator name they typed already belongs to another operator.
 *
 * Why the warning matters: de-hub migration 065 dropped the unique index on
 * `operators.name` (the upstream operator master legitimately holds eight
 * names under two different codes, so `operator_code` is the unique key now).
 * The server therefore no longer rejects a duplicate name, and nothing but
 * this check stands between an admin and an accidental look-alike row.
 *
 * Kept deliberately dumb and dependency-free: this is a HINT, never a gate.
 * If it ever disagrees with the server the server wins.
 *
 * One known-harmless divergence from the Python original: Python's `\w` is
 * unicode-aware while JavaScript's is ASCII-only, so accented characters
 * normalize differently. Operator names in this data set are ASCII upper-case,
 * and a false-positive/false-negative warning is cosmetic either way.
 *
 * @param {string} name — display name as typed
 * @returns {string} the normalized comparison key ("" for null/blank input)
 */
export const normalizeLookupName = (name) =>
  String(name ?? "")
    .toLowerCase()
    // Drop punctuation (anything that isn't a word char or whitespace).
    .replace(/[^\w\s]/g, " ")
    // Collapse all whitespace runs to a single space, then trim.
    .replace(/\s+/g, " ")
    .trim();

/**
 * Find the rows in `rows` whose display name normalizes to the same key as
 * `name`, ignoring the row currently under edit.
 *
 * Used by the Admin → Operators modal to surface a non-blocking
 * "already exists" warning, by Admin → AOIs to block one, and by Admin →
 * Landmen (which passes `nameKey="full_name"`). Returns [] for a blank name so
 * an empty form is never flagged.
 *
 * @param {string} name             — display name as typed
 * @param {Array<object>} rows      — candidate rows, each with `{ id, [nameKey] }`
 * @param {string|number} [ignoreId]— row being edited; excluded from matches
 * @param {string} [nameKey="name"] — the column holding the display name.
 *   Defaults to `name`, which is what the operators / aois / entities masters
 *   call it; `warwick_landman` calls it `full_name`.
 * @returns {Array<object>} matching rows, in the order given
 */
export const findNameMatches = (name, rows, ignoreId, nameKey = "name") => {
  const key = normalizeLookupName(name);
  if (!key) return [];
  return (Array.isArray(rows) ? rows : []).filter(
    (row) =>
      row &&
      // Loose compare: ids arrive as numbers from the API but the caller may
      // hold a string.
      String(row.id) !== String(ignoreId) &&
      normalizeLookupName(row[nameKey]) === key
  );
};

export default normalizeLookupName;
